import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { ErrorBoundary } from "./ErrorBoundary";
import { LayoutDashboard, Users, Presentation, SearchCheck, Megaphone, PenLine, Mail, Code2, Image, Video, CalendarDays, Calculator, ShieldCheck, Share2, LogOut, Coins, Menu, X, Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BrandSwitcher } from "./BrandSwitcher";
import { BrandProvider } from "../context/BrandContext";

const NAV = [
  ["/", "Dashboard", LayoutDashboard],
  ["/employees", "AI Employees", Users],
  ["/research", "Research & PPT", Presentation],
  ["/auditor", "SEO AEO GEO", SearchCheck],
  ["/ads", "Ads Manager", Megaphone],
  ["/content", "Content Studio", PenLine],
  ["/email", "Email Studio", Mail],
  ["/website", "Website Builder", Code2],
  ["/image", "Image Studio", Image],
  ["/video", "Video Studio", Video],
  ["/social", "Social Media", Share2],
  ["/scheduler", "Scheduler", CalendarDays],
  ["/pricing", "Pricing & Margins", Calculator],
];

const Sidebar = ({ open, onClose }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = user?.isAdmin ? [...NAV, ["/admin", "Admin Panel", ShieldCheck]] : NAV;
  return (
    <aside className={`fixed top-0 left-0 z-40 h-screen w-64 bg-[#0B1120]/95 backdrop-blur-xl border-r border-slate-800 flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
      <div className="h-16 flex items-center justify-between px-5 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <img src="/logo-mark.png" alt="NamoAI Digital" className="h-9 w-auto" data-testid="sidebar-logo" />
          <span className="font-display font-bold text-lg tracking-tight">NamoAI <span className="text-orange-500">Digital</span></span>
        </div>
        <button className="lg:hidden text-slate-400" onClick={onClose} data-testid="sidebar-close-btn"><X size={20} /></button>
      </div>
      <nav className="flex-1 overflow-y-auto scroll-thin py-4 px-3 space-y-1">
        {items.map(([to, label, Icon]) => (
          <NavLink key={to} to={to} end={to === "/"} onClick={onClose} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-200 ${isActive ? "bg-orange-500/10 text-orange-400 border-l-2 border-orange-500" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"}`}>
            <Icon size={17} /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-slate-800">
        <p className="text-sm font-medium text-slate-200 truncate" data-testid="sidebar-user-name">{user?.name}</p>
        <p className="text-xs text-slate-500 truncate">{user?.email}</p>
        <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }} className="mt-3 flex items-center gap-2 text-xs text-slate-400 hover:text-orange-400">
          <LogOut size={14} /> Log out
        </button>
      </div>
    </aside>
  );
};

const Topbar = ({ onMenu }) => {
  const { user, setBuyOpen } = useAuth();
  const low = !user?.isAdmin && (user?.credits ?? 0) < 20;
  return (
    <header className="h-16 sticky top-0 z-30 bg-[#070B14]/85 backdrop-blur-md border-b border-slate-800 px-3 sm:px-6 flex items-center justify-between gap-2 sm:gap-3">
      <button className="lg:hidden text-slate-300" onClick={onMenu} data-testid="sidebar-open-btn"><Menu size={22} /></button>
      <BrandSwitcher />
      <p className="hidden xl:block text-sm text-slate-400">Plan: <span className="text-slate-200 font-medium" data-testid="topbar-plan">{user?.isAdmin ? "Owner" : user?.plan}</span></p>
      <div className="flex items-center gap-2 ml-auto">
        {user?.isAdmin && <span data-testid="demo-mode-badge" className="hidden md:inline badge-o">Demo mode</span>}
        <div data-testid="credits-balance" className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-mono ${low ? "border-red-500/40 text-red-400" : "border-orange-500/30 text-orange-400"} bg-slate-900`}>
          <Coins size={15} /> <span data-testid="credits-balance-value">{user?.isAdmin ? "Unlimited" : (user?.credits ?? 0).toLocaleString()}</span><span className="hidden sm:inline">credits</span>
        </div>
        <button data-testid="buy-plan-btn" onClick={() => setBuyOpen(true)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium transition-colors">
          <Plus size={15} /><span className="hidden sm:inline">Buy Plan</span>
        </button>
      </div>
    </header>
  );
};

const ImpersonationBar = () => {
  const { user, impersonating, exitImpersonation } = useAuth();
  if (!impersonating) return null;
  return (
    <div data-testid="impersonation-bar" className="bg-sky-500/15 border-b border-sky-500/30 text-sky-200 text-sm px-4 py-2 flex items-center justify-between gap-3">
      <span className="truncate">Viewing sub-account <b>{user?.email}</b></span>
      <button data-testid="exit-impersonation-btn" onClick={exitImpersonation} className="shrink-0 px-3 py-1 rounded-full bg-sky-500 text-white text-xs">Back to admin</button>
    </div>
  );
};

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  return (
    <BrandProvider>
    <div className="min-h-screen grain">
      <Sidebar open={open} onClose={() => setOpen(false)} />
      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="lg:pl-64 relative z-10">
        <ImpersonationBar />
        <Topbar onMenu={() => setOpen(true)} />
        <main className="p-4 sm:p-6 lg:p-10 max-w-[1500px]">
          <ErrorBoundary key={pathname}><Outlet /></ErrorBoundary>
        </main>
      </div>
    </div>
    </BrandProvider>
  );
}
